<template>
    <div class="container container--devis-list">

        <Loader v-if="!isReady"></Loader>

        <List v-else
            :filtering="this"
            :itemNameSingular="'devis'"
            :itemNamePlural="'devis'" >

            <template v-slot:actions>
                    
                <Button 
                    class=".native"
                    :disabled="!hasContent"
                    :loading="processingXlsx"
                    @click.native="downloadXlsx">
                    <span class="fa fa-file-excel-o"></span>
                    Télécharger
                </Button>
                
            </template>

            <template v-slot:filters>

                <ListFilter class="filter--main">
                    <input v-model="filters.query"
                        type="text"
                        placeholder="Rechercher un n°, engagement, etc."/>
                </ListFilter>
                <ClientFilter v-model="filters.client_id"
                    :clients="clients"/>
                <SiteFilter v-model="filters.site_id"
                    :clientAddresses="clientAddresses"
                    :markets="markets" />
                <ListFilter class="filter">
                    <Select v-model="filters.status"
                        :options="status"
                        all="Tous les états">
                    </Select>
                </ListFilter>
                <DateFilter v-model="dates" />

            </template>

            <template v-slot:heading>
                <div class="cell cell--main criteria">
                    <Sorter criteria="numDisplayed">Numéro</Sorter>
                </div>
                <div class="cell criteria">
                    <Sorter criteria="clientRaisonSociale">Client</Sorter>
                </div>
                <div class="cell">
                    Commandes
                </div>
                <div class="cell criteria">                                
                    <Sorter criteria="engagementNum">Ref. client</Sorter>
                </div>
                <div class="cell criteria">
                    <Sorter criteria="createdAt">Émise le</Sorter>
                </div>
                <div class="cell criteria">
                    <Sorter criteria="endAt">Fin de validité</Sorter>
                </div>
                <div class="cell">
                    État
                </div>
                <div class="cell cell--price criteria">
                    <Sorter criteria="amountHT">Total HT</Sorter>
                </div>
                <div class="cell  cell--price criteria">                                                              
                    <Sorter criteria="amountTTC">Total TTC</Sorter>
                </div>  
                <div class="cell cell--actions">
                    Actions
                </div>          
            </template>

            <ul class="list__content devis-list" v-if="hasContent">

                <li class="list__content__item"   
                    v-for="devis in items"
                    :key="devis.id">

                    <ListItem class="list-item--card"
                        :item="devis"
                        :route="route">
                        <div class="cell cell--main">
                            <strong>{{ devis.numDisplayed }}</strong>
                        </div>
                        <div class="cell cell--secondary">{{ devis.clientRaisonSociale }}</div>
                        <div class="cell cell--secondary">
                            <ul v-if="devis.commandes.length">
                                <li v-for="commande in devis.commandes"
                                    :key="commande.id">
                                    <router-link :to="{ name: 'commande', params: { id: commande.id }}">
                                        {{ commande.numDisplayed }} <span v-if="commande.num">(En cours)</span>
                                    </router-link>
                                </li>
                            </ul>
                            <span v-if="devis.isPurchased && !devis.commandes.length">-</span>
                            <span v-if="!devis.isPurchased && !devis.commandes.length">Aucune</span>
                        </div>
                        <div class="cell cell--secondary">{{ devis.engagementNum }}</div>
                        <div class="cell cell-order-date cell--date"><Date :date="devis.createdAt" /></div>
                        <div class="cell cell-order-date cell--date cell--secondary"><Date v-if="devis.endAt" :date="devis.endAt" /></div>
                        <div class="cell cell--state">
                            <Badge type="warning" v-if="isProcessing(devis)">En cours</Badge>
                            <Badge type="info" v-if="isDevisReady(devis)">Reçu</Badge>
                            <Badge type="success" v-if="devis.isPurchased">Commandé</Badge>
                        </div>
                        <div class="cell cell--price"><Price :amount="devis.amountHT" :currency="devis.currency" /></div>
                        <div class="cell cell--price"><Price :amount="devis.amountTTC" :currency="devis.currency" /></div>
                        <div class="cell cell--actions">
                            <button class="woocommerce-button button alt"
                                type="button"
                                v-if="devis.isPurchasable && $can('COMMANDER')"
                                v-on:click="$event.preventDefault(); purchase(devis)">Commander</button>
                        </div>
                    </ListItem>

                </li>

            </ul>

            <div class="container_list container_list--no-result" v-else>
                <h1 class="woocommerce-order-details__title" v-show="!isFiltering">Aucun devis</h1>
            </div>

            <div class="list__next" v-if="hasMore">
                <Loader v-if="isLoadingMore"></Loader>
                <button
                    class="button"
                    type="button"
                    @click="next"
                    v-else>Voir les devis suivants</button>
            </div>

        </List>

    </div>
</template>

<script>
import { mapState } from 'vuex'

import { goTo } from '@/router'

import Badge from '@/components/badge/Badge.vue'
import Button from '@/components/button/Button.vue'
import ClientFilter from '@/components/list/filters/ClientFilter.vue'
import Date from '@/components/date/Date.vue'
import DateFilter from "@/components/list/filters/DateFilter.vue"
import List, { ListFilter, ListItem, Sorter } from '@/components/list/List.vue'
import Loader from '@/components/loader/Loader.vue'
import Price from '@/components/price/Price.vue'
import Select  from '@/components/select/Select.vue'
import SiteFilter from '@/components/list/filters/SiteFilter.vue'

import api from '@/api/proxyWeb/devis'
import { routes } from '@/router'
import filtering from "@/containers/filtering"

import  { useClientAddresses } from '@/helpers/clientAddress'

export default {

    name: 'DevisList',

    data: function() {
        return {
            filters: {
                client_id: null,
                from: null,
                to: null,
                query: '',
                site_id: null,
                status: null

            },
            route: routes.DEVIS,
            processingXlsx: false
        }
    },

    computed: {

        status: () => {
            return [
                { value: 'processing', name: 'En cours de traitement' },
                { value: 'ready', name: 'Reçu' },
                { value: 'purchased', name: 'Commandé' }
            ]
        },
        
        ...mapState({

            clients: state => state.clients.all,

            clientAddresses: function(state) {
                return useClientAddresses(state.clients.all, this.filters.client_id)
            },

            markets: state => state.sites.markets
        })
    },

    methods: {

        downloadXlsx() {
            this.processingXlsx = true
            api.getXlsx(this.params)
                .then(() => { this.$notify.success('Téléchargement en cours...') })
                .finally(() => { this.processingXlsx = false } )
        }, 

        isProcessing: function(devis) {
            return !devis.isReady
        },

        isDevisReady: function(devis) {
            return ( devis.isReady && ! devis.isPurchased )
        },

        purchase: function(devis) {
            goTo( this.$config.SITE_URL + '/wp-content/plugins/myAdelya/tasks/purchase/devis/' + devis.id )
        }
    },
  
    created () {        
        this.useFilter(api)
    },

    components: {
        Badge,
        Button,
        ClientFilter,
        Date,
        DateFilter,
        List,
        ListItem,
        ListFilter,
        Loader,
        Price,
        Select,
        SiteFilter,
        Sorter
    },

    mixins: [ filtering ]
}
</script>

<style scoped>

.woocommerce-orders-table ul {
    margin-bottom: 0;
}

.woocommerce-orders-table .button:not(:last-child) {
    margin-right: 1rem;
}

.table {
    width: 100%;
}

.table__action {
    color: #17a2b8;
}

.table__data {}

.table__data--main {
    font-weight: 700;
}

.table__data--no-result {
    text-align: center;
    opacity: 0.8;
}

.table__row {
    cursor: pointer;
}

</style>
